import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Search, Building2, MapPin, Phone, Pencil, Trash2, ExternalLink, Users, Target, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatDate, initials } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { CompanyFormDialog } from "@/components/CompanyFormDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export default function Companies() {
  const { meta, user } = useAuth();
  const canWrite = user?.role !== "ACCOUNTS";
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [industry, setIndustry] = useState("");
  const [status, setStatus] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 200 });
    if (search) params.set("search", search);
    if (industry) params.set("industry", industry);
    if (status) params.set("customer_status", status);
    const { data } = await api.get(`/companies?${params}`);
    setItems(data.items); setLoading(false);
  }, [search, industry, status]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!routeId) return;
    (async () => {
      try {
        const { data } = await api.get(`/companies/${routeId}`);
        setDetail(data);
      } catch { toast.error("Company not found"); }
    })();
  }, [routeId]);

  const openDetail = async (c) => {
    const { data } = await api.get(`/companies/${c.id}`);
    setDetail(data);
  };

  const doDelete = async () => { await api.delete(`/companies/${confirm.id}`); toast.success("Company deleted"); setConfirm(null); load(); };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-2xl font-bold tracking-tight text-slate-900">Companies</h1><p className="mt-0.5 text-sm text-slate-500">{items.length} companies</p></div>
        {canWrite && <Button data-testid="new-company-button" onClick={() => { setEdit(null); setFormOpen(true); }} className="gap-1.5 font-semibold"><Plus className="h-4 w-4" /> Add Company</Button>}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="companies-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search companies…" className="h-10 pl-9" />
        </div>
        <Select value={industry || "all"} onValueChange={(v) => setIndustry(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="companies-filter-industry" className="h-10 w-44"><SelectValue placeholder="Industry" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All industries</SelectItem>{(meta?.industries || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="companies-filter-status" className="h-10 w-44"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{(meta?.customer_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-44 rounded-xl" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState testid="companies-empty" icon={Building2} title="No companies yet" description={canWrite ? "Add your first company to start building your customer database." : "No companies match the current filters."} action={canWrite ? <Button onClick={() => setFormOpen(true)} className="gap-1.5"><Plus className="h-4 w-4" /> Add Company</Button> : null} />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => (
            <div key={c.id} data-testid={`company-card-${c.id}`} className="group cursor-pointer rounded-xl border border-slate-200 bg-white p-5 transition-all duration-200 hover:border-slate-300 hover:shadow-md" onClick={() => openDetail(c)}>
              <div className="flex items-start justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl text-sm font-bold text-white" style={{ background: "linear-gradient(135deg,#0057A8,#0284C7)" }}>{initials(c.name)}</div>
                <StatusBadge value={c.customer_status} type="customer" />
              </div>
              <h3 className="mt-3 truncate text-base font-semibold text-slate-800">{c.name}</h3>
              <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">{c.industry && <span>{c.industry}</span>}{c.location && <><span>·</span><MapPin className="h-3 w-3" />{c.location}</>}</div>
              <div className="mt-4 flex items-center justify-between border-t border-slate-50 pt-3">
                <span className="flex items-center gap-1.5 text-xs text-slate-500"><Target className="h-3.5 w-3.5" /> {c.lead_count} leads</span>
                <div className="flex items-center gap-1 opacity-0 transition group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
                  {canWrite && <button onClick={() => { setEdit(c); setFormOpen(true); }} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>}
                  {canWrite && <button onClick={() => setConfirm(c)} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <CompanyFormDialog open={formOpen} onOpenChange={setFormOpen} company={edit} onSaved={load} />
      <ConfirmDialog open={!!confirm} onOpenChange={() => setConfirm(null)} title="Delete company?" description="This will remove the company record. Leads will keep their company name." confirmText="Delete" onConfirm={doDelete} />

      <Sheet open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); if (routeId) navigate("/companies", { replace: true }); } }}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          {detail && <CompanyDetail c={detail} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function CompanyDetail({ c }) {
  return (
    <div className="space-y-5 pt-2">
      <div className="flex items-center gap-3">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl text-lg font-bold text-white" style={{ background: "linear-gradient(135deg,#0057A8,#0284C7)" }}>{initials(c.name)}</div>
        <div><h2 className="text-lg font-bold text-slate-900">{c.name}</h2><StatusBadge value={c.customer_status} type="customer" /></div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total Value" value={formatINR(c.total_business_value)} />
        <Stat label="Open Opps" value={c.open_opportunities} />
        <Stat label="Total Leads" value={c.lead_count} />
      </div>

      <div className="space-y-2 rounded-xl border border-slate-200 p-4 text-sm">
        {[["Industry", c.industry], ["Location", c.location], ["Website", c.website], ["Primary Contact", c.primary_contact], ["Phone", c.phone], ["Email", c.email], ["GST", c.gst_number], ["Assigned", c.assigned_salesperson_name]].filter(([, v]) => v).map(([k, v]) => (
          <div key={k} className="flex justify-between"><span className="text-slate-400">{k}</span><span className="font-medium text-slate-700">{v}</span></div>
        ))}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-800">Leads ({c.leads?.length || 0})</h3>
        {(!c.leads || c.leads.length === 0) ? <p className="text-sm text-slate-400">No leads linked</p> : (
          <div className="space-y-2">
            {c.leads.map((l) => (
              <div key={l.id} className="flex items-center justify-between rounded-lg border border-slate-100 p-2.5">
                <div><div className="text-sm font-medium text-slate-700">{l.name}</div><div className="font-mono text-xs text-slate-400">{l.lead_code}</div></div>
                <div className="text-right"><div className="font-mono text-sm font-semibold text-slate-700">{formatINR(l.estimated_value)}</div><StatusBadge value={l.stage} className="scale-90" /></div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">Projects, invoices & receivables will appear here in Phase 2</div>
    </div>
  );
}

function Stat({ label, value }) {
  return <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 text-center"><div className="font-mono text-base font-bold text-slate-900">{value}</div><div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div></div>;
}
